import React, { useState, useEffect } from 'react';
import type { FormData } from '../../types/form';
import type { AlternativeMethodCallbacks } from '../../types/verification';
import Title from '../ui/Title';
import Description from '../ui/Description';
import Button from '../ui/Button';
import ErrorMessage from '../ui/ErrorMessage';
import TryAnotherWay from '../ui/TryAnotherWay';
import Select from '../ui/Select';
import Input from '../ui/Input';
import { formatOfferCode } from '../../utils/validation';
import { useHubspot } from '../../hooks/useHubspot';

const US_STATES = [
  ['AL', 'Alabama'],
  ['AK', 'Alaska'],
  ['AZ', 'Arizona'],
  ['AR', 'Arkansas'],
  ['CA', 'California'],
  ['CO', 'Colorado'],
  ['CT', 'Connecticut'],
  ['DE', 'Delaware'],
  ['FL', 'Florida'],
  ['GA', 'Georgia'],
  ['HI', 'Hawaii'],
  ['ID', 'Idaho'],
  ['IL', 'Illinois'],
  ['IN', 'Indiana'],
  ['IA', 'Iowa'],
  ['KS', 'Kansas'],
  ['KY', 'Kentucky'],
  ['LA', 'Louisiana'],
  ['ME', 'Maine'],
  ['MD', 'Maryland'],
  ['MA', 'Massachusetts'],
  ['MI', 'Michigan'],
  ['MN', 'Minnesota'],
  ['MS', 'Mississippi'],
  ['MO', 'Missouri'],
  ['MT', 'Montana'],
  ['NE', 'Nebraska'],
  ['NV', 'Nevada'],
  ['NH', 'New Hampshire'],
  ['NJ', 'New Jersey'],
  ['NM', 'New Mexico'],
  ['NY', 'New York'],
  ['NC', 'North Carolina'],
  ['ND', 'North Dakota'],
  ['OH', 'Ohio'],
  ['OK', 'Oklahoma'],
  ['OR', 'Oregon'],
  ['PA', 'Pennsylvania'],
  ['RI', 'Rhode Island'],
  ['SC', 'South Carolina'],
  ['SD', 'South Dakota'],
  ['TN', 'Tennessee'],
  ['TX', 'Texas'],
  ['UT', 'Utah'],
  ['VT', 'Vermont'],
  ['VA', 'Virginia'],
  ['WA', 'Washington'],
  ['WV', 'West Virginia'],
  ['WI', 'Wisconsin'],
  ['WY', 'Wyoming'],
  ['DC', 'District of Columbia'],
] as const;

const stateOptions = [
  { value: '', label: '-- Select your state --' },
  ...US_STATES.map(([code, name]) => ({
    value: code,
    label: name,
  })),
];

type ManualVerificationFormProps = AlternativeMethodCallbacks & {
  formData: FormData;
  loading: boolean;
  error: string | null;
  onSubmit: (
    data: Partial<FormData>
  ) => Promise<{ success: boolean; status?: string; error?: string }>;
  onBack: () => void;
  onChange?: (data: Partial<FormData>) => void;
  showOfferCode?: boolean;
  methodExhausted?: boolean;
  smsExhausted?: boolean;
};

export default function ManualVerificationForm({
  formData,
  loading,
  error,
  onSubmit,
  onBack,
  onChange,
  showOfferCode = false,
  methodExhausted = false,
  smsExhausted = false,
  onChooseSms,
  onChooseKba,
}: ManualVerificationFormProps) {
  const [manualData, setManualData] = useState({
    offerCode: '',
    firstName: '',
    lastName: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipCode: '',
  });

  useEffect(() => {
    if (onChange) {
      onChange(manualData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualData]);

  const [zipError, setZipError] = useState<string | null>(null);
  const hubspot = useHubspot();

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    if (name === 'zipCode') {
      const numericValue = value.replace(/\D/g, '').slice(0, 5);
      setManualData((prev) => ({ ...prev, [name]: numericValue }));

      if (numericValue.length > 0 && !/^\d{5}$/.test(numericValue)) {
        setZipError('Please enter a valid 5-digit ZIP code');
      } else {
        setZipError(null);
      }
    } else if (name === 'offerCode') {
      setManualData((prev) => ({ ...prev, offerCode: formatOfferCode(value) }));
    } else {
      setManualData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    hubspot.trackEvent('manual_verification_submitted', {
      email: formData.email,
      form_step: 'manual_verification',
      first_name: manualData.firstName,
      last_name: manualData.lastName,
      address: manualData.address1,
      city: manualData.city,
      state: manualData.state,
      zip: manualData.zipCode,
    });

    try {
      const result = await onSubmit(manualData);

      if (result.status === 'accepted' || result.status === 'accepted_tier2') {
        return;
      }
    } catch {
      return;
    }
  };

  const handleBack = () => {
    hubspot.trackEvent('manual_verification_cancelled', {
      email: formData.email,
      form_step: 'manual_verification',
    });

    onBack();
  };

  const isFormValid = () => {
    const required = [
      'firstName',
      'lastName',
      'address1',
      'city',
      'state',
      'zipCode',
    ];
    return (
      required.every(
        (field) => manualData[field as keyof typeof manualData].trim() !== ''
      ) &&
      !zipError &&
      /^\d{5}$/.test(manualData.zipCode)
    );
  };

  return (
    <form
      id="manual-verification-form"
      name="manual-verification-form"
      onSubmit={handleSubmit}
      className="space-y-6"
    >
      <div className="text-center">
        <Title
          id="manual-verification-title"
          name="manual-verification-title"
          as="h1"
          className="mb-2"
        >
          Manual Verification
        </Title>
        <Description
          id="manual-verification-description"
          name="manual-verification-description"
        >
          Please provide additional information to verify your identity
        </Description>
      </div>

      <div className="space-y-4">
        {showOfferCode && (
          <Input
            id="manual-verification-offer-code"
            name="offerCode"
            value={manualData.offerCode}
            onChange={handleChange}
            label="Offer Code"
            placeholder="000-AAA-000000"
            maxLength={14}
            required
          />
        )}

        <Input
          id="manual-verification-first-name"
          name="firstName"
          value={manualData.firstName}
          onChange={handleChange}
          label="First Name"
          required
        />

        <Input
          id="manual-verification-last-name"
          name="lastName"
          value={manualData.lastName}
          onChange={handleChange}
          label="Last Name"
          required
        />

        <Input
          id="manual-verification-address1"
          name="address1"
          value={manualData.address1}
          onChange={handleChange}
          label="Street Address"
          required
        />

        <Input
          id="manual-verification-address2"
          name="address2"
          value={manualData.address2}
          onChange={handleChange}
          label="Apt/Suite"
          optional
        />

        <Input
          id="manual-verification-city"
          name="city"
          value={manualData.city}
          onChange={handleChange}
          label="City"
          required
        />

        <Select
          id="manual-verification-state"
          name="state"
          value={manualData.state}
          onChange={handleChange}
          options={stateOptions}
          label="State"
          required
        />

        <Input
          id="manual-verification-zip"
          name="zipCode"
          value={manualData.zipCode}
          onChange={handleChange}
          label="ZIP Code"
          maxLength={5}
          inputMode="numeric"
          pattern="\d{5}"
          error={zipError}
          required
        />
      </div>

      <ErrorMessage message={error} />

      <TryAnotherWay
        show={!!error || methodExhausted}
        onChooseSms={onChooseSms}
        onChooseKba={onChooseKba}
      />

      <div className="space-y-4">
        {!methodExhausted && (
          <Button
            id="manual-verification-submit"
            name="manual-verification-submit"
            type="submit"
            disabled={loading || !isFormValid()}
            loading={loading}
            loadingText="Verifying..."
            fullWidth
            size="lg"
          >
            Submit
          </Button>
        )}

        {!smsExhausted && (
          <Button
            id="manual-verification-back"
            name="manual-verification-back"
            type="button"
            onClick={handleBack}
            variant="outline"
            fullWidth
          >
            Try Phone Verification Again
          </Button>
        )}
      </div>
    </form>
  );
}
